import usePracticeGuidelines from 'components/Patients/PatientGuidelines/PracticeGuidelines/usePracticeGuidelines'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { TrackingConditionsTypes } from 'consts/giConsts'
import { GuidelineCategories, GuidelineItems } from 'consts/hiToolsConsts'

const TrackingDetailsToTrackingTag = {
  [TrackingConditionsTypes.AlignersFit]: {
    itemKey: GuidelineItems.AlignerFit
  },
  [TrackingConditionsTypes.AlignerUnseating]: {
    itemKey: GuidelineItems.AlignerUnseating
  },
  [TrackingConditionsTypes.AlignerSlightlyUnseating]: {
    itemKey: GuidelineItems.AlignerSlightlyUnseating
  },
  [TrackingConditionsTypes.AlignerSignificantlyUnseating]: {
    itemKey: GuidelineItems.AlignerSignificantlyUnseating
  },
  [TrackingConditionsTypes.MissingAttachments]: {
    itemKey: GuidelineItems.MissingAttachments
  },
  [TrackingConditionsTypes.LastAligner]: {
    itemKey: GuidelineItems.LastAligner
  },
  [TrackingConditionsTypes.PassiveWire]: {
    itemKey: GuidelineItems.ReadinessForWireChange
  },
  [TrackingConditionsTypes.WireOutOfBracket]: {
    itemKey: GuidelineItems.BrokenWireWireOutOfBracketBrokenChainMissingOTie
  },
  [TrackingConditionsTypes.BrokenChain]: {
    itemKey: GuidelineItems.BrokenWireWireOutOfBracketBrokenChainMissingOTie
  },
  [TrackingConditionsTypes.BrokenWire]: {
    itemKey: GuidelineItems.BrokenWireWireOutOfBracketBrokenChainMissingOTie
  },
  [TrackingConditionsTypes.MissingBracket]: {
    itemKey: GuidelineItems.BrokenWireWireOutOfBracketBrokenChainMissingOTie
  },
  [TrackingConditionsTypes.MissingOTie]: {
    itemKey: GuidelineItems.BrokenWireWireOutOfBracketBrokenChainMissingOTie
  },
  [TrackingConditionsTypes.BadHygiene]: {
    itemKey: GuidelineItems.PlaqueInflamationDetected
  },
  [TrackingConditionsTypes.CritiallyBadHygiene]: {
    itemKey: GuidelineItems.CriticalBadOralHygiene
  },
  [TrackingConditionsTypes.TxGoalReached]: {
    itemKey: GuidelineItems.WhenATxGoalIsReached
  },
  [TrackingConditionsTypes.RPELowCompliance]: {
    category: GuidelineCategories.RPE,
    itemKey: GuidelineItems.LowPatientCompliance
  },
  [TrackingConditionsTypes.ElasticLowCompliance]: {
    category: GuidelineCategories.Elastics,
    itemKey: GuidelineItems.LowPatientCompliance
  },
  [TrackingConditionsTypes.IPRReady]: {
    itemKey: GuidelineItems.WhenReachingAlignerNumber
  }
}

/**
 * Filters practice guideline categories based on the provided tracking details.
 *
 * It maps tracking detail keys to tags, then filters the guideline categories
 * to include only the items that are relevant to those tags.
 *
 * @param {Object} params
 * @param {Record<string, any>} params.trackingDetails - An object where keys represent tracked condition types.
 *
 * @returns {Object} result
 * @returns {Object} result.categories - Processed categories data returned from `usePracticeGuidelines`.
 * @returns {Object} result.practiceGuidelines - The raw practiceGuidelines object.
 * @returns {Object} result.filteredCategoriesByTrackingDetails - Guideline categories filtered by tracking details.
 */
export default ({ trackingDetails }) => {
  const { practiceGuidelines } = useSelector(state => state.multiPracticeReducer)

  const parsedCategories = JSON.parse(practiceGuidelines.categories || '{}')

  const trackingDetailsAsTags = Object.keys(trackingDetails).map(
    trackingKey => TrackingDetailsToTrackingTag[trackingKey]
  )

  const filteredCategoriesByTrackingDetails = useMemo(
    () =>
      Object.keys(parsedCategories).reduce((acc, categoryKey) => {
        const currentCategoryItems = Object.keys(parsedCategories?.[categoryKey]?.items || {}) || []

        const filteredItems = currentCategoryItems.reduce((itemsAcc, itemKey) => {
          const relatedTag = trackingDetailsAsTags.find(tag => tag?.itemKey?.includes(itemKey))
          if (relatedTag && (!relatedTag.category || relatedTag.category === categoryKey)) {
            return {
              ...itemsAcc,
              [itemKey]: parsedCategories[categoryKey].items[itemKey]
            }
          }

          return itemsAcc
        }, {})

        if (isEmpty(filteredItems)) {
          return acc
        }

        return {
          ...acc,
          [categoryKey]: {
            ...parsedCategories[categoryKey],
            items: filteredItems
          }
        }
      }, {}),
    [parsedCategories, trackingDetailsAsTags]
  )

  const { categories } = usePracticeGuidelines({
    categoriesData: filteredCategoriesByTrackingDetails
  })

  return {
    categories,
    practiceGuidelines,
    filteredCategoriesByTrackingDetails
  }
}
